import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode, useCallback } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { useDisconnect } from 'wagmi';
import { userService, User } from '../services/userService';

// Generate SIWE message
const generateSiweMessage = (address: string, nonce: string): string => {
  const domain = window.location.host;
  const origin = window.location.origin;
  const statement = 'Sign in to Sebear RWA Platform';
  const issuedAt = new Date().toISOString();

  return `${domain} wants you to sign in with your Ethereum account:
${address}

${statement}

URI: ${origin}
Version: 1
Chain ID: 1
Nonce: ${nonce}
Issued At: ${issuedAt}`;
};

// Generate random nonce
const generateNonce = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Check if stored token is valid for address
const getStoredToken = (address: string): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;

  // Token format is message:signature
  // Check if the message contains the current address
  try {
    const [message] = token.split(':signature:');
    if (message && message.toLowerCase().includes(address.toLowerCase())) {
      return token;
    }
  } catch {
    // Invalid token format
  }
  return null;
};

// Types
interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  needsSignature: boolean;
}

interface AuthContextType extends AuthState {
  login: (walletAddress: string) => Promise<User>;
  logout: () => void;
  refreshUser: (walletAddress: string) => Promise<User | null>;
  clearError: () => void;
  isAdmin: () => boolean;
  signIn: () => Promise<void>;
}

// Action types
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_NEEDS_SIGNATURE'; payload: boolean };

// Initial state
const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  needsSignature: false,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        error: null,
        needsSignature: false,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        error: null,
        needsSignature: false,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SET_NEEDS_SIGNATURE':
      return { ...state, needsSignature: action.payload };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const lastAuthenticatedAddress = useRef<string | null>(null);

  // Sign in with SIWE
  const signIn = useCallback(async () => {
    if (!address) {
      dispatch({ type: 'SET_ERROR', payload: 'No wallet connected' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      // Generate message
      const nonce = generateNonce();
      const message = generateSiweMessage(address, nonce);

      // Request signature
      const signature = await signMessageAsync({ message });

      // Store token as message:signature:signature (using :signature: as delimiter to handle multi-line messages)
      const token = `${message}:signature:${signature}`;
      localStorage.setItem('authToken', token);

      // Now fetch/create user
      try {
        const existingUser = await userService.getUserByWallet(address);
        dispatch({ type: 'SET_USER', payload: existingUser });
        lastAuthenticatedAddress.current = address;
      } catch {
        // Create new user
        const newUser = await userService.createUser({
          wallet_address: address,
          role: 'investor',
        });
        dispatch({ type: 'SET_USER', payload: newUser });
        lastAuthenticatedAddress.current = address;
      }
    } catch (error) {
      console.error('Sign in failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [address, signMessageAsync]);

  // Auto-authenticate when wallet connects
  useEffect(() => {
    const authenticateUser = async () => {
      if (isConnected && address) {
        // Only process if address changed
        if (lastAuthenticatedAddress.current !== address) {
          dispatch({ type: 'SET_LOADING', payload: true });

          // Check for existing valid token
          const existingToken = getStoredToken(address);

          if (existingToken) {
            // Token exists, try to get user
            try {
              const user = await userService.getUserByWallet(address);
              dispatch({ type: 'SET_USER', payload: user });
              lastAuthenticatedAddress.current = address;
              console.log('Auto-authenticated user with existing token:', user);
            } catch {
              console.log('No existing user found for wallet:', address);
              dispatch({ type: 'SET_NEEDS_SIGNATURE', payload: true });
            }
          } else {
            // No valid token, need signature
            console.log('No valid token found, signature required');
            dispatch({ type: 'SET_NEEDS_SIGNATURE', payload: true });
            // Auto-trigger sign in
            dispatch({ type: 'SET_LOADING', payload: false });
            // Give UI time to render, then auto-prompt for signature
            setTimeout(() => {
              signIn();
            }, 500);
            return;
          }

          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else if (!isConnected) {
        dispatch({ type: 'LOGOUT' });
        lastAuthenticatedAddress.current = null;
      }
    };

    authenticateUser();
  }, [isConnected, address, signIn]);

  // Login function (legacy - now uses signIn)
  const login = async (walletAddress: string): Promise<User> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      console.log('Starting login process for wallet:', walletAddress);

      // Check for existing token
      const existingToken = getStoredToken(walletAddress);

      if (!existingToken) {
        // Need to sign first
        await signIn();
      }

      // Try to get existing user first
      try {
        const existingUser = await userService.getUserByWallet(walletAddress);
        console.log('Existing user found:', existingUser);
        dispatch({ type: 'SET_USER', payload: existingUser });
        return existingUser;
      } catch {
        console.log('No existing user found, creating new user for wallet:', walletAddress);

        // Create new user
        const newUser = await userService.createUser({
          wallet_address: walletAddress,
          role: 'investor', // Default role
        });
        console.log('New user created:', newUser);
        dispatch({ type: 'SET_USER', payload: newUser });
        return newUser;
      }
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Logout function
  const logout = () => {
    console.log('Logging out user');
    userService.logout();
    dispatch({ type: 'LOGOUT' });
    lastAuthenticatedAddress.current = null;
    // Clear any stored authentication data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    // Disconnect the wallet
    disconnect();
  };

  // Refresh user data
  const refreshUser = async (walletAddress: string): Promise<User | null> => {
    try {
      const user = await userService.getUserByWallet(walletAddress);
      dispatch({ type: 'SET_USER', payload: user });
      return user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      dispatch({ type: 'SET_USER', payload: null });
      return null;
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Check if user is admin
  const isAdmin = () => {
    return state.user?.role === 'admin';
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshUser,
    clearError,
    isAdmin,
    signIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
